<template>
  <v-card>
    <v-card-title class="d-flex align-center flex-wrap">
      <v-btn
        icon
        :ripple="false"
        @click="moveWeek(-1)"
      >
        <v-icon>{{ icons.mdiChevronLeft }}</v-icon>
      </v-btn>
      <v-btn
        icon
        :ripple="false"
        @click="moveWeek(1)"
      >
        <v-icon>{{ icons.mdiChevronRight }}</v-icon>
      </v-btn>
      <span class="text-h6 me-3">{{ weekTitle }}</span>
      <v-btn
        small
        outlined
        :ripple="false"
        @click="goThisWeek"
      >
        今週
      </v-btn>
      <v-btn
        v-if="canManage"
        small
        color="primary"
        class="ms-3"
        :ripple="false"
        @click="openCastWeek(null)"
      >
        <v-icon
          left
          size="16"
        >
          {{ icons.mdiPlus }}
        </v-icon>
        週まとめ登録
      </v-btn>
      <v-btn
        v-if="canManage"
        small
        outlined
        class="ms-2"
        :ripple="false"
        @click="openAddDialog(null)"
      >
        1日分の登録
      </v-btn>
      <v-btn
        v-if="canManage"
        icon
        :ripple="false"
        title="必要人数の設定"
        @click="isStaffingDialogOpen = true"
      >
        <v-icon>{{ icons.mdiCogOutline }}</v-icon>
      </v-btn>
      <v-spacer></v-spacer>
      <span class="wb-legend">
        <span class="wb-legend__item"><span class="wb-legend__swatch wb-legend__swatch--shift" />出勤</span>
        <span class="wb-legend__item"><span class="wb-legend__swatch wb-legend__swatch--rsv" />予約指名あり</span>
        <span class="wb-legend__item"><span class="wb-legend__swatch wb-legend__swatch--absent" />休み</span>
        <span class="wb-legend__item"><span class="wb-legend__swatch wb-legend__swatch--undecided" />未定</span>
      </span>
    </v-card-title>

    <v-card-text
      v-if="isLoading"
      class="text-center py-12 loading-muted"
    >
      読み込み中...
    </v-card-text>
    <v-card-text
      v-else
      class="pt-0"
    >
      <div class="wb-scroll">
        <div class="wb-row wb-row--head">
          <button
            v-for="day in weekDays"
            :key="`head-${day.date}`"
            type="button"
            class="wb-head"
            :class="{ 'wb-head--today': day.isToday }"
            title="タップでこの日の日別予約（予約ボード・出勤）を開く"
            @click="$emit('open-day', day.date)"
          >
            <span class="wb-head__date">{{ day.headLabel }}</span>
            <span class="wb-head__meta">
              <span :class="{ 'wb-head__meta--warn': day.isShort }">
                出勤 {{ day.entries.length }}<template v-if="day.required != null">/{{ day.required }}</template>
              </span>
              <span v-if="day.reservationCount > 0">予約 {{ day.reservationCount }}</span>
            </span>
            <span class="wb-head__open">日別予約を開く ›</span>
          </button>
        </div>
        <div class="wb-row">
          <div
            v-for="day in weekDays"
            :key="`col-${day.date}`"
            class="wb-col"
            :class="{ 'wb-col--today': day.isToday }"
          >
            <component
              :is="canManage ? 'button' : 'div'"
              v-for="entry in day.entries"
              :key="`chip-${entry.schedule.id}`"
              type="button"
              class="cast-chip"
              :class="{ 'cast-chip--rsv': entry.hasRsv, 'cast-chip--tappable': canManage }"
              :title="canManage ? `タップで ${entry.schedule.userName} の週まとめ登録` : null"
              @click="canManage && openCastWeek(entry.schedule)"
            >
              <b>{{ chipTime(entry.schedule) }}</b>
              <span>{{ entry.schedule.userName }}<template v-if="entry.hasRsv">
                <span class="cast-chip__mark">●予約</span>
              </template></span>
            </component>
            <div
              v-for="absent in day.absences"
              :key="`absent-${absent.id}`"
              class="cast-chip cast-chip--absent"
              title="キャストアプリで「休み」と回答"
            >
              <b>休み</b>
              <span>{{ absent.userName }}</span>
            </div>
            <div
              v-for="undecided in day.undecideds"
              :key="`undecided-${undecided.id}`"
              class="cast-chip cast-chip--undecided"
              title="キャストアプリで「未定」と回答"
            >
              <b>未定</b>
              <span>{{ undecided.userName }}</span>
            </div>
            <button
              v-if="canManage"
              type="button"
              class="wb-add"
              @click="openAddDialog(day.date)"
            >
              ＋ シフト
            </button>
          </div>
        </div>
      </div>
    </v-card-text>

    <!-- キャスト週まとめ登録（チップ経由はキャスト選択済み、ヘッダー経由は未選択で開く） -->
    <cast-week-schedule-dialog
      v-model="isCastWeekOpen"
      :casts="casts"
      :initial-cast-id="castWeekInitialId"
      :week-dates="weekDateStrs"
      :schedules="schedules"
      :nominated-user-ids-by-date="nominatedUserIdsByDateProp"
      @saved="fetchWeek"
    />

    <!-- シフト登録（ヘッダー: 日付選択式 / ＋シフト: 日付固定） -->
    <v-dialog
      v-model="isAddDialogOpen"
      width="480"
    >
      <v-card>
        <v-card-title>シフト登録</v-card-title>
        <v-card-text class="pb-6">
          <shift-bulk-add-form
            v-if="isAddDialogOpen"
            :key="`add-${addFormDate || 'free'}`"
            :casts="casts"
            :fixed-date="addFormDate"
            :excluded-user-ids="addFormExcludedUserIds"
            @created="onShiftAdded"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 必要人数設定 -->
    <staffing-target-dialog
      v-model="isStaffingDialogOpen"
      :targets="staffingTargets"
      @saved="fetchWeek"
    />
  </v-card>
</template>

<script>
import {
  ref, computed, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiChevronLeft, mdiChevronRight, mdiPlus, mdiCogOutline,
} from '@mdi/js'
import { format, addDays } from 'date-fns'
import WorkScheduleApi from '@/api/waiter/WorkSchedule'
import StaffingTargetApi from '@/api/waiter/StaffingTarget'
import ReservationApi from '@/api/waiter/Reservation'
import UserApi from '@/api/waiter/User'
import useCurrentData from '@/views/composable/useCurrentData'
import useDateFormat from '@/views/composable/useDateFormat'
import { resolveRequiredCount, countApprovedByDate } from '@/views/waiter/shiftStaffingUtils'
import ShiftBulkAddForm from '@/views/waiter/settings/ShiftBulkAddForm.vue'
import StaffingTargetDialog from '@/views/waiter/settings/StaffingTargetDialog.vue'
import CastWeekScheduleDialog from '@/views/waiter/settings/CastWeekScheduleDialog.vue'

export default {
  components: {
    ShiftBulkAddForm,
    StaffingTargetDialog,
    CastWeekScheduleDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { currentUser } = useCurrentData()
    const { hhmm } = useDateFormat()

    const canManage = computed(() => {
      if (['waiter', 'admin'].includes(currentUser.value?.role)) return true

      return vm.$can('read', 'custom:settings_work_schedule_manage')
    })

    const isLoading = ref(false)
    const schedules = ref([])
    const staffingTargets = ref([])
    const reservations = ref([])
    const casts = ref([])

    // 日曜始まり（月カレンダーと同じ）
    const startOfWeek = date => addDays(date, -date.getDay())
    const weekStart = ref(startOfWeek(new Date()))

    const weekDateStrs = computed(() => Array.from({ length: 7 }, (unused, i) => format(addDays(weekStart.value, i), 'yyyy-MM-dd')))

    const fetchWeek = async () => {
      isLoading.value = true
      try {
        const startStr = weekDateStrs.value[0]
        const endStr = weekDateStrs.value[6]
        const [schedulesRes, targetsRes, reservationsRes] = await Promise.all([
          WorkScheduleApi.getWorkSchedules({ status: 'approved', startDate: startStr, endDate: endStr }),
          StaffingTargetApi.getStaffingTargets().catch(() => null),
          // 予約は「指名あり色変え・件数・ロック判定」に使う。詳細は不要
          ReservationApi.getReservations({ from: startStr, to: endStr }).catch(() => null),
        ])

        schedules.value = schedulesRes?.data
          ? schedulesRes.data.work_schedules.data.map(d => ({ id: d.id, ...d.attributes }))
          : []
        staffingTargets.value = targetsRes?.data
          ? targetsRes.data.staffing_targets.data.map(d => ({
            id: Number(d.id),
            dayOfWeek: d.attributes.dayOfWeek,
            date: d.attributes.date ? String(d.attributes.date).slice(0, 10) : null,
            requiredCount: d.attributes.requiredCount,
          }))
          : []
        reservations.value = reservationsRes?.data
          ? reservationsRes.data.reservations.data
            .map(d => ({ id: d.id, ...d.attributes }))
            .filter(r => r.status !== 'canceled')
          : []
      } finally {
        isLoading.value = false
      }
    }

    const getCasts = async () => {
      const res = await UserApi.getUsers(['cast'])
      if (res?.data) {
        casts.value = res.data.users.data.map(u => ({ id: Number(u.id), name: u.attributes?.name || `ID:${u.id}` }))
      }
    }

    const approvedByDate = computed(() => countApprovedByDate(schedules.value.filter(s => !s.requestType || s.requestType === 'work')))

    // 日付 → その日に指名予約があるキャストの userId 集合
    const nominatedUserIdsByDate = computed(() => reservations.value.reduce((acc, r) => {
      const key = String(r.reservedDate).slice(0, 10)
      if (!acc[key]) acc[key] = new Set();
      (r.nominations || []).forEach(n => acc[key].add(Number(n.userId)))

      return acc
    }, {}))

    const todayStr = format(new Date(), 'yyyy-MM-dd')

    const weekDays = computed(() => weekDateStrs.value.map(dateStr => {
      const date = new Date(dateStr)
      const dayNames = ['日', '月', '火', '水', '木', '金', '土']
      const required = resolveRequiredCount(staffingTargets.value, dateStr)
      const nominatedIds = nominatedUserIdsByDate.value[dateStr] || new Set()

      const daySchedules = schedules.value.filter(s => String(s.date).slice(0, 10) === dateStr)

      // 出勤（work。旧データはrequestType未設定=work扱い）を開始時刻順に並べる（時間未定は末尾）
      const entries = daySchedules
        .filter(s => !s.requestType || s.requestType === 'work')
        .slice()
        .sort((a, b) => {
          if (!a.startAt && !b.startAt) return 0
          if (!a.startAt) return 1
          if (!b.startAt) return -1

          return a.startAt < b.startAt ? -1 : 1
        })
        .map(schedule => ({ schedule, hasRsv: nominatedIds.has(Number(schedule.userId)) }))

      // キャストアプリの回答: 休み(absent) / 未定(undecided)。未回答（行なし）と区別して表示する
      const absences = daySchedules.filter(s => s.requestType === 'absent')
      const undecideds = daySchedules.filter(s => s.requestType === 'undecided')

      return {
        absences,
        undecideds,
        date: dateStr,
        headLabel: `${date.getMonth() + 1}/${date.getDate()}（${dayNames[date.getDay()]}）`,
        isToday: dateStr === todayStr,
        required,
        isShort: required != null && (approvedByDate.value[dateStr] || 0) < required,
        reservationCount: reservations.value.filter(r => String(r.reservedDate).slice(0, 10) === dateStr).length,
        entries,
      }
    }))

    const weekTitle = computed(() => {
      const start = new Date(weekDateStrs.value[0])
      const end = new Date(weekDateStrs.value[6])

      return `${start.getMonth() + 1}/${start.getDate()} 〜 ${end.getMonth() + 1}/${end.getDate()}`
    })

    const chipTime = schedule => (schedule.startAt ? `${hhmm(schedule.startAt)}〜${schedule.endAt ? hhmm(schedule.endAt) : ''}` : '時間未定')

    const moveWeek = diff => {
      weekStart.value = addDays(weekStart.value, diff * 7)
      fetchWeek()
    }

    const goThisWeek = () => {
      weekStart.value = startOfWeek(new Date())
      fetchWeek()
    }

    // ── キャスト週まとめ登録 ──
    const isCastWeekOpen = ref(false)
    const castWeekInitialId = ref(null)

    // props で渡すため Set を配列に変換する
    const nominatedUserIdsByDateProp = computed(() => Object.fromEntries(
      Object.entries(nominatedUserIdsByDate.value).map(([date, ids]) => [date, [...ids]]),
    ))

    // schedule あり = チップ経由（そのキャストを選択済みで開く）/ null = ヘッダー経由（未選択で開く）
    const openCastWeek = schedule => {
      castWeekInitialId.value = schedule ? Number(schedule.userId) : null
      isCastWeekOpen.value = true
    }

    // ── シフト登録 / 必要人数 ──
    const isAddDialogOpen = ref(false)
    const addFormDate = ref(null)

    const addFormExcludedUserIds = computed(() => {
      if (!addFormDate.value) return []

      return schedules.value
        .filter(s => String(s.date).slice(0, 10) === addFormDate.value)
        .map(s => Number(s.userId))
    })

    const openAddDialog = dateStr => {
      addFormDate.value = dateStr
      isAddDialogOpen.value = true
    }

    const onShiftAdded = async () => {
      isAddDialogOpen.value = false
      await fetchWeek()
    }

    const isStaffingDialogOpen = ref(false)

    fetchWeek()
    getCasts()

    return {
      canManage,
      isLoading,
      casts,
      staffingTargets,
      weekTitle,
      weekDateStrs,
      weekDays,
      chipTime,
      moveWeek,
      goThisWeek,
      fetchWeek,
      isCastWeekOpen,
      castWeekInitialId,
      nominatedUserIdsByDateProp,
      schedules,
      openCastWeek,
      isAddDialogOpen,
      addFormDate,
      addFormExcludedUserIds,
      openAddDialog,
      onShiftAdded,
      isStaffingDialogOpen,
      icons: {
        mdiChevronLeft,
        mdiChevronRight,
        mdiPlus,
        mdiCogOutline,
      },
    }
  },
}
</script>

<style scoped>
.loading-muted {
  color: #94a3b8;
}

.wb-legend {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #94a3b8;
}

.wb-legend__item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.wb-legend__swatch {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.wb-legend__swatch--shift {
  background: rgba(34, 211, 238, 0.18);
  border: 1px solid rgba(34, 211, 238, 0.6);
}

.wb-legend__swatch--rsv {
  background: rgba(244, 114, 182, 0.18);
  border: 1px solid rgba(244, 114, 182, 0.6);
}

.wb-legend__swatch--absent {
  background: rgba(148, 163, 184, 0.2);
  border: 1px solid rgba(148, 163, 184, 0.55);
}

.wb-legend__swatch--undecided {
  background: transparent;
  border: 1px dashed rgba(148, 163, 184, 0.7);
}

.wb-scroll {
  overflow-x: auto;
}

.wb-row {
  display: grid;
  grid-template-columns: repeat(7, minmax(128px, 1fr));
}

.wb-row--head {
  border-bottom: 1px solid rgba(148, 163, 184, 0.3);
}

.wb-head {
  padding: 8px 8px 6px;
  border: none;
  border-left: 1px solid rgba(148, 163, 184, 0.15);
  background: transparent;
  text-align: left;
  cursor: pointer;
  min-width: 0;
  color: inherit;
}

.wb-head:hover .wb-head__date {
  text-decoration: underline;
}

.wb-head__date {
  display: block;
  font-size: 13px;
  font-weight: 700;
}

.wb-head--today .wb-head__date {
  color: #a60df2;
}

.wb-head__meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: #94a3b8;
}

.wb-head__meta--warn {
  color: #ef4444;
  font-weight: 700;
}

.wb-head__open {
  font-size: 10.5px;
  color: #a60df2;
}

.wb-col {
  border-left: 1px solid rgba(148, 163, 184, 0.15);
  padding: 8px 6px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  min-height: 220px;
}

.wb-col--today {
  background: rgba(166, 13, 242, 0.05);
}

.cast-chip {
  border: 1px solid rgba(34, 211, 238, 0.6);
  background: rgba(34, 211, 238, 0.14);
  border-radius: 7px;
  padding: 4px 8px;
  font-size: 11.5px;
  text-align: left;
  width: 100%;
  line-height: 1.4;
  color: inherit;
}

/* キャストアプリの回答（休み/未定）。出勤チップと区別して薄く表示 */
.cast-chip--absent {
  border-color: rgba(148, 163, 184, 0.5);
  background: rgba(148, 163, 184, 0.14);
  color: #94a3b8;
}

.cast-chip--undecided {
  border: 1px dashed rgba(148, 163, 184, 0.6);
  background: transparent;
  color: #94a3b8;
}

.cast-chip--tappable {
  cursor: pointer;
}

.cast-chip--rsv {
  border-color: rgba(244, 114, 182, 0.65);
  background: rgba(244, 114, 182, 0.14);
}

.cast-chip b {
  display: block;
  white-space: nowrap;
}

.cast-chip span {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cast-chip__mark {
  color: #f472b6;
  font-weight: 700;
}

.wb-add {
  border: 1px dashed rgba(148, 163, 184, 0.5);
  background: transparent;
  border-radius: 7px;
  padding: 5px;
  font-size: 11.5px;
  color: #94a3b8;
  cursor: pointer;
  margin-top: auto;
}
</style>
